import { isAxiosError } from "axios";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Badge } from "@/core/components/ui/badge";
import { Card, CardContent } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { PayBreakdownPaneShell } from "@/payroll/pay-breakdown/pay-breakdown-pane-shell";
import { usePayPlan } from "@/payroll/pay-breakdown/pay-plan-query";
import { PayPlanMarkdown } from "@/payroll/pay-plan-markdown";
import { PAYROLL_EDITOR_PATH } from "@/payroll/settings/routes";

interface PayBreakdownPayPlanTabProps {
  businessId: string;
  runId: string;
  /**
   * Whether this user may edit the rules, decided by the server from the
   * feature flag and their authority. The page already holds the answer, so
   * this pane takes it rather than asking again.
   */
  canEditRules?: boolean;
}

export const PayBreakdownPayPlanTab = ({
  businessId,
  runId,
  canEditRules = false,
}: PayBreakdownPayPlanTabProps) => {
  const { data, isLoading, isError, error } = usePayPlan(businessId, runId);
  const planDocument = data?.plan_document ?? "";
  const hasPlanDocument = planDocument.trim().length > 0;
  const isForbidden = isAxiosError(error) && error.response?.status === 403;

  return (
    <PayBreakdownPaneShell>
      <Card className="mx-auto flex min-h-[400px] w-full min-w-0 max-w-[864px] flex-col rounded-lg border-stroke-tertiary bg-surface-primary shadow-none">
        {data && hasPlanDocument && (
          <Stack
            direction="row"
            align="center"
            gap="sm"
            fullWidth
            className="shrink-0 border-b border-stroke-tertiary px-4 py-3 sm:px-8"
          >
            <Badge variant="gray" size="sm">
              Script v{data.ai_script_version_number}
            </Badge>
            <Typography variant="bodyW3" textColor="secondary">
              The pay plan used by this period's payroll run.
            </Typography>
            {/* "Current", not "these". This page can be showing an archived
                version that paid a period months ago, and the editor always
                works on the rules in force today. */}
            {canEditRules && (
              <Link to={PAYROLL_EDITOR_PATH} className="ml-auto shrink-0">
                <Typography variant="bodyW3" textColor="interactive">
                  Change current rules
                </Typography>
              </Link>
            )}
          </Stack>
        )}

        <CardContent className="flex flex-1 flex-col p-0">
          {isLoading ? (
            <CenteredState>
              <LoadingSpinner />
            </CenteredState>
          ) : isError ? (
            <CenteredState>
              <Typography variant="subtitleW1">
                {isForbidden
                  ? "Pay plan access unavailable"
                  : "Pay plan could not be loaded"}
              </Typography>
              <Typography textColor="secondary">
                {isForbidden
                  ? "You aren't authorized to view the pay plan for this payroll run."
                  : "Try again after refreshing the page."}
              </Typography>
            </CenteredState>
          ) : !hasPlanDocument ? (
            <CenteredState>
              <Typography variant="subtitleW1">
                No pay plan available
              </Typography>
              <Typography textColor="secondary">
                This payroll run doesn't have a pay plan document.
              </Typography>
            </CenteredState>
          ) : (
            <Stack fullWidth gap="none" className="px-4 py-6 sm:px-8">
              <PayPlanMarkdown document={planDocument} className="w-full" />
            </Stack>
          )}
        </CardContent>
      </Card>
    </PayBreakdownPaneShell>
  );
};

const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <Stack
    align="center"
    justify="center"
    gap="xs"
    className="min-h-[400px] w-full flex-1 px-6 text-center"
  >
    {children}
  </Stack>
);
